<script setup>
// Use an array to store multiple messages
const responseMessages = useState('responseMessages', () => []);
const isVisible = ref(false);
const route = useRoute();

const DISPLAY_DURATION = 5000;
const PROGRESS_UPDATE_INTERVAL = 100; // Update progress every 100ms

// Watch for route changes to clear all messages
watch(
  () => route.path,
  () => {
    clearAllMessages();
  }
);

// Watch for changes to add new messages
watch(
  () => useState('responseMessage').value?.key,
  (newKey, oldKey) => {
    const newMessage = useState('responseMessage').value;

    if (newMessage?.message) {
      // Add new message to the stack with a unique ID and timeout info
      const messageWithId = {
        ...newMessage,
        id: Date.now(),
        timeoutId: null,
        progressId: null,
        progress: 100,
        height: 0,
      };

      // Add to start of array so newest messages appear at top
      responseMessages.value.unshift(messageWithId);
      isVisible.value = true;

      // Set timeout for auto-removal if not permanent
      if (!newMessage.doesNotExpire) {
        // Start progress countdown
        messageWithId.progressId = setInterval(() => {
          const index = responseMessages.value.findIndex(msg => msg.id === messageWithId.id);
          if (index !== -1) {
            const message = responseMessages.value[index];
            message.progress = Math.max(0, message.progress - (PROGRESS_UPDATE_INTERVAL / DISPLAY_DURATION * 100));
          }
        }, PROGRESS_UPDATE_INTERVAL);

        messageWithId.timeoutId = setTimeout(() => {
          removeMessage(messageWithId.id);
        }, DISPLAY_DURATION);
      }

      // Reset the original responseMessage
      useState('responseMessage').value = {};
    }
  },
  { immediate: true }
);

function removeMessage(id) {
  const index = responseMessages.value.findIndex(msg => msg.id === id);
  if (index !== -1) {
    const message = responseMessages.value[index];

    // Clear any existing timeout and progress interval
    if (message.timeoutId) {
      clearTimeout(message.timeoutId);
    }
    if (message.progressId) {
      clearInterval(message.progressId);
    }

    // Remove the message
    responseMessages.value.splice(index, 1);

    // Hide container if no messages left
    if (responseMessages.value.length === 0) {
      isVisible.value = false;
    }
  }
}

function clearAllMessages() {
  // Clear all timeouts and intervals
  responseMessages.value.forEach(message => {
    if (message.timeoutId) {
      clearTimeout(message.timeoutId);
    }
    if (message.progressId) {
      clearInterval(message.progressId);
    }
  });

  // Clear all messages
  responseMessages.value = [];
  isVisible.value = false;
}

// Clean up timeouts and intervals when component unmounts
onBeforeUnmount(() => {
  responseMessages.value.forEach(message => {
    if (message.timeoutId) {
      clearTimeout(message.timeoutId);
    }
    if (message.progressId) {
      clearInterval(message.progressId);
    }
  });
});
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 pointer-events-none" style="z-index: 999999;">
      <Transition enter-active-class="transition ease-out duration-300"
        enter-from-class="transform translate-x-full opacity-0" enter-to-class="transform translate-x-0 opacity-100"
        leave-active-class="transition ease-in duration-300" leave-from-class="transform translate-x-0 opacity-100"
        leave-to-class="transform translate-x-full opacity-0">
        <div v-if="isVisible" class="fixed top-4 right-4 min-w-40 space-y-2 pointer-events-auto">
          <TransitionGroup enter-active-class="transition-all ease-out duration-300"
            enter-from-class="opacity-0 translate-x-8" enter-to-class="opacity-100 translate-x-0"
            leave-active-class="transition-all ease-in duration-300" leave-from-class="opacity-100 translate-x-0"
            leave-to-class="opacity-0 translate-x-8" move-class="transition-transform duration-300" tag="div"
            class="space-y-2">
            <div v-for="message in responseMessages" :key="message.id"
              class="rounded-lg shadow-lg border-l-4 overflow-hidden max-w-96 group" :class="[
                message.success
                  ? 'bg-green-50 border-green-500 text-green-800'
                  : 'bg-red-50 border-red-500 text-red-800'
              ]">
              <!-- Notification Content -->
              <div class="p-3"
                :class="!message.doesNotExpire && message === responseMessages[responseMessages.length - 1] ? 'pb-2' : ''">
                <div class="flex items-center space-x-2">
                  <!-- Admin Only Badge-->
                  <UBadge v-if="message.isAdminOnly" color="error" variant="soft" size="xs">
                    Admin Only
                  </UBadge>
                  <!-- Icon -->
                  <UIcon v-else :name="message.success ? 'i-heroicons-check-circle' : 'i-heroicons-x-circle'"
                    class="h-5 w-5" :class="message.success ? 'text-green-500' : 'text-red-500'" />
                  <!-- Message -->
                  <div class="flex-1 min-w-0">
                    <span class="text-xs sm:text-sm break-words">
                      {{ message.message }}
                    </span>
                  </div>
                  <!-- Remove button -->
                  <button
                    class="p-0.5 rounded-lg transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100"
                    :class="[
                      message.success
                        ? 'hover:bg-green-100 active:bg-green-200'
                        : 'hover:bg-red-100 active:bg-red-200'
                    ]" @click="removeMessage(message.id)">
                    <UIcon name="i-heroicons-x-mark" class="h-4 w-4"
                      :class="message.success ? 'text-green-500' : 'text-red-500'" />
                  </button>
                </div>
              </div>

              <!-- Progress bar -->
              <UProgress v-if="!message.doesNotExpire && message === responseMessages[responseMessages.length - 1]"
                v-model="message.progress" :color="message.success ? 'success' : 'error'" size="xs"
                class="[&>div]:bg-transparent -ml-0.5" />
            </div>
          </TransitionGroup>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>